import { useRef, useState } from 'react';
import { FocalPointPicker } from '../../ui/FocalPointPicker';

export interface PhotoGalleryFieldPhoto {
  url: string;
  focal?: { x: number; y: number } | null;
}

export interface PhotoGalleryFieldProps {
  photos: PhotoGalleryFieldPhoto[];
  onChange: (photos: PhotoGalleryFieldPhoto[]) => void;
  /** Uploads one file (the caller compresses + stores it) and returns its public URL, or null on failure. */
  onUpload: (file: File) => Promise<string | null>;
  /** Max photos. Default 3. */
  max?: number;
  inputClassName?: string;
}

/**
 * PhotoGalleryField — host-side editor for an ordered photo list: thumbnails
 * (first = cover, shown as the carousel's first slide), per-photo focal point,
 * remove / make-cover, upload or paste-a-URL until `max` is reached.
 */
export function PhotoGalleryField({
  photos, onChange, onUpload, max = 3,
  inputClassName = 'rounded-md border border-app-border bg-app-bg px-3 py-1.5 text-sm text-app-fg placeholder:text-app-muted focus:border-gold focus:outline-none',
}: PhotoGalleryFieldProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState(0);
  const [urlDraft, setUrlDraft] = useState('');

  const full = photos.length >= max;
  const current = photos[Math.min(selected, photos.length - 1)];
  const currentIndex = Math.min(selected, photos.length - 1);

  async function handleFiles(files: FileList | null) {
    if (!files?.length) return;
    setUploading(true);
    setFailed(false);
    let next = photos;
    for (const file of Array.from(files).slice(0, max - photos.length)) {
      const url = await onUpload(file);
      if (url) next = [...next, { url, focal: null }];
      else setFailed(true);
    }
    onChange(next);
    if (next.length > photos.length) setSelected(next.length - 1);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  function addUrl() {
    const url = urlDraft.trim();
    if (!url || full) return;
    onChange([...photos, { url, focal: null }]);
    setSelected(photos.length);
    setUrlDraft('');
  }

  function remove(i: number) {
    onChange(photos.filter((_, idx) => idx !== i));
    setSelected(0);
  }

  function makeCover(i: number) {
    onChange([photos[i], ...photos.filter((_, idx) => idx !== i)]);
    setSelected(0);
  }

  return (
    <div className="flex flex-col gap-2">
      <input ref={fileRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={(e) => void handleFiles(e.target.files)} />

      {photos.length > 0 ? (
        <div className="flex gap-2">
          {photos.map((p, i) => (
            <button
              key={p.url}
              type="button"
              onClick={() => setSelected(i)}
              className={`relative w-20 h-20 rounded-lg overflow-hidden border-2 shrink-0 ${i === currentIndex ? 'border-gold' : 'border-app-border'}`}
            >
              <img src={p.url} alt="" className="w-full h-full object-cover" style={p.focal ? { objectPosition: `${p.focal.x}% ${p.focal.y}%` } : undefined} />
              {i === 0 ? <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-caption2 py-0.5">Cover</span> : null}
            </button>
          ))}
        </div>
      ) : null}

      {current ? (
        <div className="flex flex-col gap-2">
          <FocalPointPicker
            key={current.url}
            src={current.url}
            focal={current.focal ?? { x: 50, y: 50 }}
            onChange={(f) => onChange(photos.map((p, i) => (i === currentIndex ? { ...p, focal: f } : p)))}
          />
          <div className="flex gap-3 text-xs">
            {currentIndex > 0 ? (
              <button type="button" onClick={() => makeCover(currentIndex)} className="text-app-muted hover:text-app-fg underline sk-hit">Make cover</button>
            ) : null}
            <button type="button" onClick={() => remove(currentIndex)} className="text-app-muted hover:text-crimson underline sk-hit">Remove photo</button>
          </div>
        </div>
      ) : null}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading || full}
          className="text-sm text-app-muted hover:text-app-fg border border-app-border rounded-lg px-3 py-1.5 transition-colors shrink-0 disabled:opacity-50 sk-hit"
        >
          {uploading ? 'Uploading…' : full ? `${max}/${max} photos` : `Upload photo${photos.length ? ` (${photos.length}/${max})` : 's'}`}
        </button>
        {!full ? (
          <input
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }}
            onBlur={addUrl}
            placeholder="or paste a URL"
            className={`flex-1 min-w-0 ${inputClassName}`}
          />
        ) : null}
      </div>
      {failed ? <p className="text-xs text-crimson">One or more photos couldn't be uploaded. Please try again.</p> : null}
    </div>
  );
}
